// MAJLIS-98 (UI-C19, UI-C10; found in the MAJLIS-71 proposal, consultant ruling Q4 of 2026-09-24): the mic ring reads
// the local level through an AnalyserNode on ONE AudioContext (mic.ts). After an iOS audio interruption (a call, Siri,
// another app recording) WebKit can leave that context `suspended` or `interrupted` (WebKit's extra state) while the
// microphone track itself is live and publishing; the ring then reads 0 and shows `idle` though he is being heard.
// This keeps the context running, with no visible change: whenever it is stalled, the next of these tries resume():
//   (a) the page becoming visible (visibilitychange) and the window gaining focus;
//   (b) the microphone track's `unmute` (WebKit mutes capture for an interruption and unmutes it after);
//   (c) the next user gesture, resume() called SYNCHRONOUSLY inside the handler so the browser counts it as one.
// The gesture listeners are armed only while the context is stalled and removed once it runs; the context's
// `statechange` re-arms them on a later suspension. No timer, no polling.
//
// What it never does (TS-3): create an AudioContext or call getUserMedia - it is handed the one context and the one
// track mic.ts already holds, and touches nothing else. A rejected resume() is reported (the ?micdiag=1 panel prints
// it) and the listeners stay armed for the next trigger.
//
// ASSUMPTION (sourced, not measured on a phone): WebAudio issue 2585 (2024-04-30, open) reports resume() on user
// input NOT always helping a context stuck `interrupted`; whether these triggers bring the owner's ring back is
// what his phone test shows (the panel's `audioctx resume` and `audioctx statechange` lines).

/** Why a resume() was tried. */
export type ResumeTrigger = "visible" | "focus" | "unmute" | "gesture";

/** A resume() that was tried, and its result: null when it resolved, else the error's NAME only. A resolved resume()
 * does not prove the context runs (WebKit may keep it `interrupted`); its `statechange` says that. */
export type ResumeReport = (r: { trigger: ResumeTrigger; error: string | null }) => void;

/** The part of an AudioContext this touches. */
export type ResumableContext = Pick<AudioContext, "state" | "resume" | "addEventListener" | "removeEventListener">;
/** The part of the microphone track this touches: its `unmute` event only. */
export type UnmuteSource = Pick<MediaStreamTrack, "addEventListener" | "removeEventListener">;
export type PageDocument = Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
export type PageWindow = Pick<Window, "addEventListener" | "removeEventListener">;

/** ASSUMPTION, from the HTML standard's list of activation-triggering input events ("keydown" other than Escape,
 * "mousedown", "pointerdown" for a mouse, "pointerup" for other pointers, "touchend"): on a phone a tap activates on
 * pointerup/touchend, NOT pointerdown, so all of them are listened to. Each call is guarded by the context's state, so
 * the extra events of one tap resume at most once and cost nothing while it runs. */
export const GESTURE_EVENTS = ["pointerdown", "pointerup", "mousedown", "touchend", "keydown"] as const;

/** WebKit's `interrupted` is not in lib.dom's AudioContextState, so the state is compared as a string. */
export function stalled(ctx: Pick<AudioContext, "state">): boolean {
  const s: string = ctx.state;
  return s === "suspended" || s === "interrupted";
}

function errorName(e: unknown): string {
  const name = (e as { name?: unknown } | null)?.name;
  return typeof name === "string" ? name : "Error";
}

/** Keep the ring's AudioContext running across interruptions. Returns the disposer, which removes every listener this
 * added; after it nothing here calls resume() again. */
export function keepRingRunning(
  ctx: ResumableContext,
  track: UnmuteSource | null,
  doc: PageDocument,
  win: PageWindow,
  report?: ResumeReport,
): () => void {
  let disposed = false;
  let gestureArmed = false;

  const tryResume = (trigger: ResumeTrigger): void => {
    if (disposed || !stalled(ctx)) return;
    let p: Promise<void>;
    try {
      p = ctx.resume(); // synchronous from the caller's handler: inside a gesture, this IS the gesture's call
    } catch (e) {
      report?.({ trigger, error: errorName(e) });
      return;
    }
    p.then(
      () => {
        if (!disposed) report?.({ trigger, error: null });
      },
      (e: unknown) => {
        if (!disposed) report?.({ trigger, error: errorName(e) }); // stays armed: the state did not change
      },
    );
  };

  const onGesture = () => tryResume("gesture");
  const arm = (on: boolean) => {
    if (on === gestureArmed) return;
    gestureArmed = on;
    for (const type of GESTURE_EVENTS) {
      if (on) win.addEventListener(type, onGesture, { capture: true, passive: true });
      else win.removeEventListener(type, onGesture, { capture: true });
    }
  };

  const onState = () => {
    if (!disposed) arm(stalled(ctx)); // running: the one-shot is spent; a later suspension re-arms it
  };
  const onVisible = () => {
    if (doc.visibilityState === "visible") tryResume("visible");
  };
  const onFocus = () => tryResume("focus");
  const onUnmute = () => tryResume("unmute");

  ctx.addEventListener("statechange", onState);
  doc.addEventListener("visibilitychange", onVisible);
  win.addEventListener("focus", onFocus);
  track?.addEventListener("unmute", onUnmute);
  arm(stalled(ctx));

  return () => {
    if (disposed) return;
    disposed = true;
    arm(false);
    ctx.removeEventListener("statechange", onState);
    doc.removeEventListener("visibilitychange", onVisible);
    win.removeEventListener("focus", onFocus);
    track?.removeEventListener("unmute", onUnmute);
  };
}
